// ==================== ACCOUNT MAPPING SERVICE ====================
// Extracted from database.js - contains all account mapping functionality for Saudi logistics

import { 
  collection, 
  doc, 
  getDocs, 
  addDoc, 
  updateDoc, 
  query, 
  where, 
  serverTimestamp 
} from 'firebase/firestore';
import { db } from '../../utils/database/firebase-config';

export const accountMappingService = {
  async getAccountMappings(companyId) {
    try {
      const mappingsRef = collection(db, 'accountMappings');
      const q = query(mappingsRef, where('companyId', '==', companyId));
      const snapshot = await getDocs(q);
      
      if (snapshot.empty) {
        // Initialize default mappings if none exist
        return await this.initializeDefaultMappings(companyId);
      }
      
      return snapshot.docs[0].data();
    } catch (error) {
      console.error('Error getting account mappings:', error);
      throw error;
    }
  },

  async updateAccountMappings(companyId, mappings, updatedBy) {
    try {
      const mappingsRef = collection(db, 'accountMappings');
      const q = query(mappingsRef, where('companyId', '==', companyId));
      const snapshot = await getDocs(q);
      
      const mappingData = {
        ...mappings,
        companyId,
        updatedBy,
        updatedAt: serverTimestamp()
      };
      
      if (snapshot.empty) {
        // Create new mapping document
        mappingData.createdBy = updatedBy;
        mappingData.createdAt = serverTimestamp();
        const docRef = await addDoc(mappingsRef, mappingData);
        return docRef.id;
      } else {
        // Update existing mapping
        const docRef = doc(db, 'accountMappings', snapshot.docs[0].id);
        await updateDoc(docRef, mappingData);
        return snapshot.docs[0].id;
      }
    } catch (error) {
      console.error('Error updating account mappings:', error);
      throw error;
    }
  },

  async initializeDefaultMappings(companyId, createdBy = 'system') {
    const defaultMappings = {
      companyId,
      // Revenue Accounts
      revenue: {
        logisticsRevenue: '4000',    // Logistics Service Revenue
        transportationRevenue: '4100', // Transportation Revenue
        otherRevenue: '4900'         // Other Revenue
      },
      // Expense Accounts
      expenses: {
        fuel: '5000',               // Fuel Expenses
        maintenance: '5100',        // Vehicle Maintenance
        salary: '5200',             // Salary Expenses
        commission: '5700',         // Commission Expenses
        office: '5300',             // Office Supplies
        marketing: '5400',          // Marketing Expenses
        insurance: '5500',          // Insurance Expenses
        utilities: '5600',          // Utilities
        other: '5900'               // Other Expenses
      },
      // Payroll Specific Accounts
      payroll: {
        salaryExpense: '5200',      // Salary Expense
        commissionExpense: '5700',  // Commission Expense
        bonusExpense: '5210',       // Bonus Expense
        payrollPayable: '2300',     // Payroll Payable
        salariesPayable: '2300',    // Salaries Payable (alias)
        payrollTaxPayable: '2310',  // Payroll Tax Payable
        employeeBenefits: '5220'    // Employee Benefits Expense
      },
      // Asset Accounts
      assets: {
        cash: '1000',               // Cash
        bank: '1010',               // Bank Account
        accountsReceivable: '1100', // Accounts Receivable
        vatReceivable: '1300',      // VAT Receivable
        inventory: '1200',          // Inventory
        fixedAssets: '1500'         // Fixed Assets
      },
      // Liability Accounts
      liabilities: {
        accountsPayable: '2000',    // Accounts Payable
        vatPayable: '2100',         // VAT Payable
        accruedExpenses: '2200',    // Accrued Expenses
        salariesPayable: '2300',    // Salaries Payable
        longTermDebt: '2500'        // Long-term Debt
      },
      // Equity Accounts
      equity: {
        ownersEquity: '3000',       // Owner's Equity
        retainedEarnings: '3100'    // Retained Earnings
      },
      createdBy,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    };

    const mappingsRef = collection(db, 'accountMappings');
    await addDoc(mappingsRef, defaultMappings);
    return defaultMappings;
  },

  /**
   * Get expense category to account mappings
   */
  async getExpenseCategoryMappings(companyId) {
    try {
      const mappings = await this.getAccountMappings(companyId);
      
      // Return expense category mappings with default values
      return {
        fuel: mappings.expenses?.fuel || '5000',
        maintenance: mappings.expenses?.maintenance || '5100',
        salary: mappings.expenses?.salary || '5200',
        commission: mappings.expenses?.commission || '5700',
        office: mappings.expenses?.office || '5300',
        marketing: mappings.expenses?.marketing || '5400',
        insurance: mappings.expenses?.insurance || '5500',
        utilities: mappings.expenses?.utilities || '5600',
        rent: mappings.expenses?.rent || '5800',
        professional_services: mappings.expenses?.professional_services || '5900',
        equipment: mappings.expenses?.equipment || '5950',
        oil_change: mappings.expenses?.oil_change || '5110',
        other: mappings.expenses?.other || '5999'
      };
    } catch (error) {
      console.error('Error getting expense category mappings:', error);
      return {};
    }
  },

  /**
   * Update expense category mappings
   */
  async updateExpenseCategoryMappings(companyId, categoryMappings, updatedBy) {
    try {
      const currentMappings = await this.getAccountMappings(companyId);
      
      // Update the expenses section
      const updatedMappings = {
        ...currentMappings,
        expenses: {
          ...currentMappings.expenses,
          ...categoryMappings
        }
      };

      await this.updateAccountMappings(companyId, updatedMappings, updatedBy);
      return true;
    } catch (error) {
      console.error('Error updating expense category mappings:', error);
      throw error;
    }
  },

  /**
   * Get automation settings for the company
   */
  async getAutomationSettings(companyId) {
    try {
      const mappingsRef = collection(db, 'automationSettings');
      const q = query(mappingsRef, where('companyId', '==', companyId));
      const snapshot = await getDocs(q);
      
      if (snapshot.empty) {
        // Return default automation settings if none exist
        return this.getDefaultAutomationSettings();
      }
      
      return snapshot.docs[0].data().settings || this.getDefaultAutomationSettings();
    } catch (error) {
      console.error('Error getting automation settings:', error);
      return this.getDefaultAutomationSettings();
    }
  },

  /**
   * Update automation settings for the company
   */
  async updateAutomationSettings(companyId, automationSettings, updatedBy) {
    try {
      const settingsRef = collection(db, 'automationSettings');
      const q = query(settingsRef, where('companyId', '==', companyId));
      const snapshot = await getDocs(q);
      
      const settingsData = {
        companyId,
        settings: automationSettings,
        updatedBy,
        updatedAt: serverTimestamp()
      };
      
      if (snapshot.empty) {
        // Create new automation settings document
        settingsData.createdBy = updatedBy;
        settingsData.createdAt = serverTimestamp();
        const docRef = await addDoc(settingsRef, settingsData);
        return docRef.id;
      } else {
        // Update existing settings
        const docRef = doc(db, 'automationSettings', snapshot.docs[0].id);
        await updateDoc(docRef, settingsData);
        return snapshot.docs[0].id;
      }
    } catch (error) {
      console.error('Error updating automation settings:', error);
      throw error;
    }
  },

  /**
   * Get default automation settings
   */
  getDefaultAutomationSettings() {
    return {
      commissionExpenses: {
        expenseEnabled: true,
        journalEntryEnabled: true,
        accountMapping: '5700'
      },
      maintenanceExpenses: {
        expenseEnabled: true,
        journalEntryEnabled: true,
        accountMapping: '5100'
      },
      fuelExpenses: {
        expenseEnabled: true,
        journalEntryEnabled: true,
        accountMapping: '5000'
      },
      revenueRecognition: {
        expenseEnabled: true,
        journalEntryEnabled: true,
        accountMapping: '4000'
      },
      payrollExpenses: {
        expenseEnabled: true,
        journalEntryEnabled: true,
        accountMapping: '5200'
      },
      paymentJournalEntries: {
        journalEntryEnabled: true,
        cashAccountMapping: '1000',      // Cash account
        bankAccountMapping: '1010',      // Bank account
        receivableAccountMapping: '1100' // Accounts Receivable
      },
      invoiceJournalEntries: {
        journalEntryEnabled: true,
        revenueAccountMapping: '4000',   // Revenue account
        receivableAccountMapping: '1100', // Accounts Receivable
        vatAccountMapping: '2100'        // VAT Payable
      }
    };
  },

  /**
   * Check if automation is enabled for a specific process
   */
  async isAutomationEnabled(companyId, processId, type = 'expense') {
    try {
      const settings = await this.getAutomationSettings(companyId);
      const processSettings = settings[processId];
      
      if (!processSettings) return true; // Default to enabled if not configured
      
      return type === 'expense' ? 
        processSettings.expenseEnabled : 
        processSettings.journalEntryEnabled;
    } catch (error) {
      console.error('Error checking automation settings:', error);
      return true; // Default to enabled on error
    }
  }
}; 